import React, { useState, useEffect } from 'react';
import useLockingSystemStore from '../../stores/useLockingSystemStore';
import styles from './ToolCard.module.css';

const LockingSystemDetailCard = ({ selectedSystem, onBackToModal, customizationEnabled = false }) => {
    const lockingSystemState = useLockingSystemStore((state) => state.lockingSystem);
    const setLockingSystem = useLockingSystemStore((state) => state.setLockingSystem);
    
    // Use local state for UI but sync with store
    const [diameterValue, setDiameterValue] = useState(lockingSystemState.diameter);
    const [depthValue, setDepthValue] = useState(lockingSystemState.depth);
    const [diameterFocused, setDiameterFocused] = useState(false);
    const [depthFocused, setDepthFocused] = useState(false);

    // Sync with store when component mounts or store changes
    useEffect(() => {
        setDiameterValue(lockingSystemState.diameter);
        setDepthValue(lockingSystemState.depth);
    }, [lockingSystemState.diameter, lockingSystemState.depth]);

    // Comprehensive debugging

    // Defensive programming: ensure selectedSystem is valid
    if (!selectedSystem || typeof selectedSystem !== 'object') {
        return (
            <div className={styles.toolCard}>
                <div style={{ padding: '16px', textAlign: 'center', color: '#ef4444' }}>
                    Error: Invalid locking system data
                    <br />
                    <small>Type: {typeof selectedSystem}</small>
                    <br />
                    <small>Value: {JSON.stringify(selectedSystem)}</small>
                </div>
            </div>
        );
    }

    // Safely extract values with fallbacks and type checking
    const systemName = (() => {
        const name = selectedSystem.name;
        if (typeof name === 'string') return name;
        if (typeof name === 'object') return JSON.stringify(name);
        return String(name || 'Unknown System');
    })();

    const systemBrand = (() => {
        const brand = selectedSystem.brand;
        if (typeof brand === 'string') return brand;
        if (typeof brand === 'object') return JSON.stringify(brand);
        return String(brand || 'Unknown Brand');
    })();

    const systemImage = (() => {
        const image = selectedSystem.image;
        if (typeof image === 'string') return image;
        if (typeof image === 'object') return JSON.stringify(image);
        return String(image || '/images/lock_type_custom.png');
    })();


    const positionOptions = [
        { name: 'Front Positioning', color: '#ff9800' },
        { name: 'Front Rotation', color: '#f44336' },
        { name: 'Sagittal Positioning', color: '#e91e63' },
        { name: 'Sagittal Rotation', color: '#f44336' },
        { name: 'Locking Button', color: '#f44336' }
    ];

    const handlePositionClick = (position) => {
        // Simple click handler - no selection state
    };

    const handleDiameterIncrement = () => {
        const newValue = Math.round((diameterValue + 0.1) * 10) / 10;
        setDiameterValue(newValue);
        setLockingSystem({ diameter: newValue });
    };

    const handleDiameterDecrement = () => {
        const newValue = Math.round((diameterValue - 0.1) * 10) / 10;
        setDiameterValue(newValue);
        setLockingSystem({ diameter: newValue });
    };

    const handleDepthIncrement = () => {
        const newValue = Math.round((depthValue + 0.1) * 10) / 10;
        setDepthValue(newValue);
        setLockingSystem({ depth: newValue });
    };

    const handleDepthDecrement = () => {
        const newValue = Math.round((depthValue - 0.1) * 10) / 10;
        setDepthValue(newValue);
        setLockingSystem({ depth: newValue });
    };

    const handleDiameterChange = (value) => {
        const numValue = parseFloat(value) || 0;
        const roundedValue = Math.round(numValue * 10) / 10;
        setDiameterValue(roundedValue);
        setLockingSystem({ diameter: roundedValue });
    };

    const handleDepthChange = (value) => {
        const numValue = parseFloat(value) || 0;
        const roundedValue = Math.round(numValue * 10) / 10;
        setDepthValue(roundedValue);
        setLockingSystem({ depth: roundedValue });
    };

    return (
        <div className={styles.toolCard}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '16px', paddingBottom: '10px', marginBottom: '16px' }}>
                <div className='flex justify-end'>
                    <button
                        onClick={onBackToModal}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px'
                        }}
                        title="Remove selection"
                    >
                        <img src="/tool_card_icons/scoop_recharge_trash_icon.png" alt="Delete" style={{ width: 16, height: 16 }} />
                    </button>
                </div>
                <div className='flex flex-row gap-4 justify-between'>
                    <div>
                        <h3>{customizationEnabled ? 'Custom Distal Lock' : systemName}</h3>
                        <div style={{
                            display: 'inline-block',
                            background: '#e0f2fe',
                            color: '#0288d1',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500',
                            marginBottom: '8px'
                        }}>
                            Brand
                        </div>
                        <div style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
                            {systemBrand}
                        </div>
                    </div>
                    <img src={customizationEnabled ? '/images/lock_type_custom.png' : systemImage} alt={systemName} />
                </div>

                {/* Customization Fields (if enabled) */}
                {customizationEnabled && (
                    <div style={{
                        display: 'flex',
                        gap: '16px',
                        padding: '16px 0',
                        marginBottom: '16px',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>

                        {/* Dimension Inputs */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#374151', minWidth: '80px' }}>
                                    Diameter (d):
                                </label>
                                <div style={{
                                    display: 'flex',
                                    width: '100%',
                                    alignItems: 'center',
                                    gap: 6,
                                    border: '1px solid #7c3aed',
                                    borderRadius: 6,
                                    padding: '2px 8px',
                                    background: 'white'
                                }}>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={diameterValue}
                                        onChange={(e) => handleDiameterChange(e.target.value)}
                                        onFocus={() => setDiameterFocused(true)}
                                        onBlur={() => setDiameterFocused(false)}
                                        style={{
                                            width: 60,
                                            padding: 3,
                                            border: diameterFocused ? '1px solid #7c3aed' : '1px solid #d1d5db',
                                            borderRadius: 3,
                                            fontSize: 12,
                                            textAlign: 'center',
                                            background: 'transparent',
                                            outline: 'none',
                                            fontWeight: 600,
                                            color: '#374151',
                                            boxShadow: diameterFocused ? '0 0 0 2px rgba(124, 58, 237, 0.1)' : 'none'
                                        }}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <button
                                            onClick={handleDiameterIncrement}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: 1,
                                                fontSize: 10,
                                                color: '#7c3aed'
                                            }}
                                        >
                                            ▲
                                        </button>
                                        <button
                                            onClick={handleDiameterDecrement}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: 1,
                                                fontSize: 10,
                                                color: '#7c3aed'
                                            }}
                                        >
                                            ▼
                                        </button>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>mm</span>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                                <label style={{ fontSize: '12px', color: '#374151', minWidth: '80px' }}>
                                    Depth (h):
                                </label>
                                <div style={{
                                    display: 'flex',
                                    width: '100%',
                                    alignItems: 'center',
                                    gap: 6,
                                    border: '1px solid #7c3aed',
                                    borderRadius: 6,
                                    padding: '2px 8px',
                                    background: 'white'
                                }}>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={depthValue}
                                        onChange={(e) => handleDepthChange(e.target.value)}
                                        onFocus={() => setDepthFocused(true)}
                                        onBlur={() => setDepthFocused(false)}
                                        style={{
                                            width: 60,
                                            padding: 3,
                                            border: depthFocused ? '1px solid #7c3aed' : '1px solid #d1d5db',
                                            borderRadius: 3,
                                            fontSize: 12,
                                            textAlign: 'center',
                                            background: 'transparent',
                                            outline: 'none',
                                            fontWeight: 600,
                                            color: '#374151',
                                            boxShadow: depthFocused ? '0 0 0 2px rgba(124, 58, 237, 0.1)' : 'none'
                                        }}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <button
                                            onClick={handleDepthIncrement}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: 1,
                                                fontSize: 10,
                                                color: '#7c3aed'
                                            }}
                                        >
                                            ▲
                                        </button>
                                        <button
                                            onClick={handleDepthDecrement}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: 1,
                                                fontSize: 10,
                                                color: '#7c3aed'
                                            }}
                                        >
                                            ▼
                                        </button>
                                    </div>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>mm</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '16px' }}>

                {/* Positioning Options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {positionOptions.map((option) => (
                        <button
                            key={option.name}
                            onClick={() => handlePositionClick(option.name)}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '2px solid #7c3aed',
                                borderRadius: '6px',
                                background: 'white',
                                color: '#7c3aed',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#7c3aed';
                                e.target.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = 'white';
                                e.target.style.color = '#7c3aed';
                            }}
                        >
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: option.color
                            }}></div>
                            {option.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LockingSystemDetailCard; 